/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */
/* eslint-disable react/destructuring-assignment */
/* eslint-disable no-console */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { detect } from 'detect-browser';
import PropTypes from 'prop-types';
import { ThemeProvider } from 'styled-components';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { datadogLogs } from '@datadog/browser-logs';
import _get from 'lodash/get';
import _map from 'lodash/map';
import _omit from 'lodash/omit';
import _keyBy from 'lodash/keyBy';
import _camelCase from 'lodash/camelCase';
import { PersonalizationTagTypes } from '@btag/bt-personalization';

import Editor from './components/Editor';
import Save from './components/Save';
import Hotkeys from './components/Hotkeys';
import Modals from './Modals';
import registerEvents from './common/eventService/registerEvents';
import eventService from './common/eventService';
import dynamicallyLoadCustomFonts from './common/dynamicallyLoadCustomFonts';
import saveTimeout from './common/saveTimeout';
import { request } from './common/request';
import { disableAutoSaveTimer, resetAutoSaveTimer, setIsSaveFired, startAutoSaveTrigger } from './middleware/autoSave';
import { pendoInit } from './common/pendo';
import { initializeClient } from './common/launchDarklyClient';
import handleKeyPress from './common/handleKeyPress';
import isCacheFresh from './common/isCacheFresh';
import rules from './constants/businessRules/businessRules';
import imageSources from './constants/imageSources/imageSources';
import { APPLICATION_MODES, DRAFT_ACCESS } from './constants/itemTypes/itemTypes';
import defaultElementsManager from './common/defaultItemsManager';
import defaultCustomization from './constants/customization/defaultCustomization';
import { sessionType } from './types';
import {
    connectSocket,
    listenForClients,
    draftOpened,
    listenForChanges,
    listenForSaves,
    listenForReconnect,
    listenForUnsavedChanges,
    draftSaved,
} from './actions/sockets';
import { updateVersionHistoryItemSavingStatus } from './actions/versionHistory';
import validateSession from './common/validation/validateSession';
import validatePreviewSession from './common/validation/validatePreviewSession';
import validateUserData from './common/validation/validateUserData';
import getPreviewSession from './common/session/getPreviewSession';
import bananatagTemplate from './constants/templates/bananatagTemplate';
import staffbaseTemplate from './constants/templates/staffbaseTemplate';
import { triggerFetch } from './actions/thirdPartyContent';
import * as browserActions from './actions/browser';
import * as sessionActions from './actions/session';
import * as canvasActions from './actions/canvasBlocks';
import * as customFontsActions from './actions/customFonts';
import { getSingleUseTagsError, getSingleUseTagsSuccess } from './actions/personalization';
import * as Styled from './styles';
import { COLORS } from './common/branding/colors/index';
import {
    ERR_ENDPOINT_NETWORK_GENERIC,
    ERR_CUSTOM_FONTS_LOAD,
    ERR_VALIDATE_AND_FETCH_USER_DATA,
} from './constants/Errors';

import './common/branding/fontAwesomeLibrary';
import './App.css';
import configs from './constants/configs';

/**
 * Users' session gets recorded using a third party application called Full Story. This function sets the required data
 * @param {string} integration - The integration which the user is using pikasso in
 * @param {object} userData - Information about the user
 * @param {string} userData.userID - userID
 * @param {string} userData.firstName - user's name
 * @param {string} userData.lastName - user's lastname
 * @param {string} userData.email - user's email address
 */
const _trackSessions = ({ userData, integration }) => {
    const { userID, firstName, lastName, email } = userData;
    // making sure userID is present and ignoring pikasso.io sessions
    if (
        configs.fullStoryEnabled &&
        userID &&
        email !== 'test_user_staging@pikasso.com' &&
        email !== 'test_user@pikasso.com' &&
        window.FS
    ) {
        // Full story
        window.FS.identify(userID, {
            displayName: `${firstName} ${lastName}`,
            email,
            integration,
            reviewsWritten_int: 14,
        });
    }
};

function addEvent(obj, evt, fn) {
    if (obj.addEventListener) {
        obj.addEventListener(evt, fn, false);
    } else if (obj.attachEvent) {
        obj.attachEvent(`on${evt}`, fn);
    }
}

const _timeStampConsole = () => {
    console.log(
        '%c%s',
        'color: blue; font-size: 16px;font-style: italic;font-weight: bold;text-shadow: 2px 2px lightgray;',
        '\n piKasso'
    );
    console.log(
        '%c%s',
        'color: blue; font-size: 10px; text-shadow: 1px 1px lightgray;',
        `version: ${configs.reactAppVersion}\n`
    );
};

const inIframe = () => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true;
    }
};

class App extends Component {
    state = {
        isSupported: true,
        browserWidth: +window.innerWidth,
        browserHeight: +window.innerHeight,
        browserWidthSmall: false,
        browserHeightSmall: false,
        hasError: false,
        error: null,
        info: null,
    };

    componentDidMount() {
        const { session, dispatch } = this.props;
        let browser = detect();
        if (!browser) {
            browser = {
                name: 'CANNOT_DETECT',
                version: 'CANNOT_DETECT',
                os: 'CANNOT_DETECT',
            };
        }
        const isIE = browser && browser.name === 'ie';
        dispatch(browserActions.setBrowserName(browser.name));
        dispatch(browserActions.setOSName(browser.os));
        registerEvents();
        eventService.sendIframeReady();
        _timeStampConsole();
        this._updateWindowEventListeners();
        window.onunload = () => {
            eventService.removeAllListeners();
            this._updateWindowEventListeners(true);
        };

        this.setState((prevState) => ({
            ...prevState,
            isSupported: !(isIE && parseInt(browser.version, 10) < 11),
            browserWidthSmall: window.innerWidth < rules.MINIMUM_ALLOWABLE_BROWSER_WIDTH,
            browserHeightSmall: window.innerHeight < rules.MINIMUM_ALLOWABLE_BROWSER_HEIGHT,
        }));
        addEvent(document, 'mouseout', (evt) => {
            const e = evt || window.event;
            const from = e.relatedTarget || e.toElement;
            if (!from || from.nodeName === 'HTML') {
                dispatch(browserActions.updateModifierKeyPressedStatus(false));
            }
        });
        if (configs.NODE_ENV === 'development') {
            if (session.user.features.EMAIL_DESIGNER_CUSTOM_FONTS) {
                this._loadCustomFonts();
            }
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { ldClient, session } = this.props;
        const { browserWidthSmall, browserHeightSmall } = this.state;
        if (ldClient !== undefined && session.user !== prevProps.session.user) {
            const {
                user: { email, accountID, plan },
            } = this.props.session;

            const emailDomain = email.split('@')[1]; // user@acme.com -> acme.com
            const userObj = {
                key: accountID,
                email: `common@${emailDomain}`, // temporary for backward compatibility of active rules during transition
                custom: {
                    account: accountID,
                    emailDomain,
                    isIC: true, // Only IC has access to pikasso
                    plan,
                    isPaying: true, // IC paying customers only have access to pikasso
                },
            };

            ldClient.identify(userObj); // React client
            initializeClient(userObj); // JS client (enabled when needed)
        }

        // disable auto save when on small sized window
        if (browserWidthSmall !== prevState.browserWidthSmall || browserHeightSmall !== prevState.browserHeightSmall) {
            if (browserWidthSmall || browserHeightSmall) {
                disableAutoSaveTimer();
            } else {
                resetAutoSaveTimer();
            }
        }
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        console.log({ error, info });
        this.setState({ error, info });
    }

    _updateWindowEventListeners(destroy = false) {
        const events = ['message', 'resize', 'keydown', 'keyup', 'error'];
        events.forEach((event) => {
            if (destroy) {
                window.removeEventListener(event, (eventData) => this._windowEventHandlers(event, eventData));
                window.onunload = undefined;
                return;
            }
            window.addEventListener(event, (eventData) => this._windowEventHandlers(event, eventData));
        });
    }

    _windowEventHandlers(eventName, event) {
        switch (eventName) {
            case 'message':
                return (() => {
                    this._handleIncomingMessages(event);
                })();
            case 'resize':
                return (() => {
                    this.setState((prevState) => ({
                        ...prevState,
                        browserWidth: window.innerWidth,
                        browserHeight: window.innerHeight,
                        browserWidthSmall: window.innerWidth < rules.MINIMUM_ALLOWABLE_BROWSER_WIDTH,
                        browserHeightSmall: window.innerHeight < rules.MINIMUM_ALLOWABLE_BROWSER_HEIGHT,
                    }));
                })();
            case 'keydown':
                return (() => {
                    const modifierKeys = ['ShiftLeft', 'ShiftRight', 'MetaLeft', 'MetaRight'];
                    if (modifierKeys.includes(event.code)) {
                        this.props.dispatch(browserActions.updateModifierKeyPressedStatus(true));
                    } else {
                        this.props.dispatch(browserActions.updateModifierKeyPressedStatus(false)); // to disable multiple key pressing => screws up the wysiwyg editor
                    }
                })();
            case 'keyup':
                return (() => {
                    this.props.dispatch(browserActions.updateModifierKeyPressedStatus(false));
                })();
            case 'error':
                return (() => {
                    const { message, error } = event;
                    const { session } = this.props;
                    const time = new Date();
                    const lastAction = this.props.changeLog || 'N/A';
                    let browser = detect();
                    if (!browser) {
                        browser = {
                            name: 'CANNOT_DETECT',
                            version: 'CANNOT_DETECT',
                            os: 'CANNOT_DETECT',
                        };
                    }
                    const user = `userID: ${_get(session, 'user.userID', 'N/A')}`;
                    const payload = {
                        browser,
                        time: time.toLocaleString('en-ca'),
                        user,
                        integration: session.integration,
                        errorInfo: {
                            message,
                            stack: error.stack,
                        },
                        lastAction,
                    };
                    console.error(payload);
                })();
            default:
                return null;
        }
    }

    async _validatePreviewData(session, next) {
        try {
            const validationErr = validatePreviewSession(session);
            if (validationErr) {
                next(`Preview Session Validation Error: ${validationErr.message}`);
                return;
            }

            next(null, getPreviewSession(session));
        } catch (err) {
            datadogLogs.logger.error(err);
            next(err);
        }
    }

    async _validateAndFetchUserData(session, next) {
        try {
            const validationErr = validateSession(session);
            if (validationErr) {
                next(`Session Validation Error: ${validationErr.message}`);
                return;
            }
            this.props.dispatch(sessionActions.updateAuthorization(session.authorization));
            const { tokenValid, userData, jwt } = await request.post(configs.getUserData);

            if (!tokenValid) {
                next(`Session Validation Error: invalid ${tokenValid ? 'token' : 'session'}`);
                return;
            }

            // we will not enable the user data validation to fail because we do not have
            // control over the integrity of the user data that comes from the database.
            // so we will log it, gather the information of users that are with missing
            // information and request the fix. once we have no more errors we will
            // make this fail and not open pikasso if there is a user data error.
            const validationUserDataErr = validateUserData(userData);
            if (validationUserDataErr) {
                datadogLogs.logger.warn(`User Data Validation Error: ${validationUserDataErr.message}`);
            }

            if (jwt) {
                session.authorization = `Bearer ${jwt}`;
            }

            try {
                const { clientIP } = await request.get(configs.getClientIP);
                session.userData = userData;
                session.userData.ipAddress = clientIP;
            } catch (err) {
                next(new Error(ERR_ENDPOINT_NETWORK_GENERIC));
                return;
            }

            next(null, session);
        } catch (err) {
            next(new Error(err.customMessage ? err.customMessage : ERR_VALIDATE_AND_FETCH_USER_DATA));
        }
    }

    async _socketConnect(session) {
        const {
            collaboration: { type, id, versionID, trackID },
            user,
            userData,
            authorization,
        } = session;
        const authenticatedUser = userData || user;
        const jwt = authorization.split(' ')[1];
        const connectedUser = {
            firstName: authenticatedUser.firstName,
            lastName: authenticatedUser.lastName,
            email: authenticatedUser.email,
            ipAddress: session.userData.ipAddress,
            draftId: id,
            accountId: authenticatedUser.accountID,
        };
        const documentID = (type === 'template' ? 't' : 'd') + id + versionID + trackID;
        await this.props.connectSocket(jwt, authenticatedUser.plan);
        await this.props.listenForClients();
        await this.props.listenForReconnect(connectedUser);
        await this.props.draftOpened(connectedUser);
        if (authenticatedUser.plan === 'IC_LEGACY') {
            this.props.dispatch(sessionActions.setLoading(false));
        } else {
            await this.props.listenForUnsavedChanges();
            await this.props.listenForChanges();
        }
        await this.props.listenForSaves({
            documentID,
        });
    }

    async _checkForCachedFont(fontID, url) {
        const cacheStorage = await caches.open(`font-${fontID}`);
        const cachedMatch = await cacheStorage.match(url);
        // If cache is found and is fresh, return from cache, otherwise fetch from url
        if (!cachedMatch || !isCacheFresh(cachedMatch)) {
            const dataRequest = await request.get(url);
            const cachedResponse = new Response(JSON.stringify(dataRequest), {
                headers: {
                    'content-type': 'application/json',
                    date: new Date().getTime(),
                },
            });

            if (!cachedResponse || !cachedResponse.ok) {
                return false;
            }
            // Add/replace the font in the cache storage
            cacheStorage.put(url, cachedResponse);
            return dataRequest;
        }

        return cachedMatch.json();
    }

    async _loadCustomFonts() {
        const { dispatch, flags } = this.props;

        try {
            const customFontFamilies = await request.get(configs.getAccountCustomFontFamilies);

            // load all custom font families metadata async, order is not important
            const fontFamiliesData = await Promise.all(
                customFontFamilies.map((fontFamilyID) =>
                    request
                        .get(`${configs.getCustomFontFamily}?fontFamilyID=${fontFamilyID}&includeFonts=true`)
                        .then((data) => ({
                            fontFamilyData: data,
                            fontFamilyID,
                        }))
                )
            );

            fontFamiliesData.map(({ fontFamilyID, fontFamilyData }) =>
                dispatch(
                    customFontsActions.updateCustomFonts({
                        fontFamilyID,
                        fontFamilyData,
                    })
                )
            );

            await Promise.all(
                fontFamiliesData.map(({ fontFamilyID, fontFamilyData }) =>
                    dispatch(
                        customFontsActions.updateCustomFonts({
                            fontFamilyID,
                            fontFamilyData,
                        })
                    )
                )
            );

            dispatch(customFontsActions.setInfoLoadedStatus(true));

            // load all custom fonts data async and handle data to avoid dup data in redux e.g. fontFamilyID
            // we are also making it follow the current pattern: { fontFamilyID: { metadata, fonts: { fontID: { data }, fontID: { data } } } }
            const fontFamiliesAndFontsData = await Promise.all(
                fontFamiliesData.map(async ({ fontFamilyID, fontFamilyData }) => ({
                    fontFamilyData: {
                        ...fontFamilyData,
                        fonts: _keyBy(
                            _map(
                                await Promise.all(
                                    fontFamilyData.fonts.map(async ({ fontID }) => {
                                        const url = `${configs.getCustomFont}?fontID=${fontID}&includeFontData=true`;
                                        if (flags.ed477CustomFontCaching) {
                                            const fontData = await this._checkForCachedFont(fontID, url);
                                            return {
                                                fontID,
                                                fontData,
                                            };
                                        }
                                        return {
                                            fontID,
                                            fontData: await request.get(url),
                                        };
                                    })
                                ),
                                ({ fontData, fontID }) => ({
                                    ..._omit(fontData, 'fontFamilyID'),
                                    fontID,
                                })
                            ),
                            'fontID'
                        ),
                    },
                    fontFamilyID,
                }))
            );

            // wait until all the custom fonts are loaded into redux
            await Promise.all(
                fontFamiliesAndFontsData.map(({ fontFamilyID, fontFamilyData }) =>
                    dispatch(
                        customFontsActions.updateCustomFonts({
                            fontFamilyID,
                            fontFamilyData,
                        })
                    )
                )
            );

            // custom fonts loaded into redux
            dispatch(customFontsActions.setLoadedStatus(true));

            dynamicallyLoadCustomFonts(_map(fontFamiliesAndFontsData, 'fontFamilyData'));
        } catch (err) {
            console.error(err);
            eventService.notify(err.customMessage ? err.customMessage : ERR_CUSTOM_FONTS_LOAD, true);
        }
    }

    async _handleIncomingMessages(event) {
        const { data } = event;

        if (data.target === 'pikasso') {
            const { type, payload } = data;
            if (type === 'init' && this.props.session.loading) {
                const { params } = JSON.parse(payload);
                const { blocks, ...session } = params;
                if (session.integration === 'PUBLIC_PREVIEW') {
                    this._validatePreviewData(session, (err, sessionData) => {
                        if (err) {
                            eventService.notify(err, true);
                            return;
                        }

                        this.props.dispatch(
                            sessionActions.setSession({
                                ...sessionData,
                                loading: true,
                            })
                        );
                        this.props.dispatch(
                            canvasActions.populateInitialBlocks({
                                initialBlocksState:
                                    blocks ||
                                    (session.template === 'BANANATAG' ? bananatagTemplate : staffbaseTemplate),
                            })
                        );
                        eventService.sendMounted();
                        this.props.dispatch(sessionActions.setLoading(false));
                    });
                } else {
                    try {
                        const token = JSON.parse(event.data.payload).params.authorization;
                        this.props.dispatch(sessionActions.updateAuthorization(token));
                    } catch (err) {
                        console.log('Error setting initial JWT', err);
                    }
                    this._validateAndFetchUserData(session, (err, sessionData) => {
                        if (err) {
                            eventService.notify(err, true);
                            datadogLogs.logger.error(err);
                            return;
                        }
                        const userCustomization = _get(sessionData, 'userData.customization', defaultCustomization);
                        const customization = {
                            ...defaultCustomization,
                            ...userCustomization,
                        };
                        defaultElementsManager.updateItems = _get(sessionData, 'userData.items');
                        defaultElementsManager.updateFonts = customization;

                        this.props.dispatch(
                            browserActions.appModeChanged(
                                sessionData.collaboration.access === DRAFT_ACCESS.READ
                                    ? APPLICATION_MODES.PREVIEW
                                    : APPLICATION_MODES.EDITOR
                            )
                        );

                        this.props.dispatch(
                            sessionActions.setSession({
                                ...sessionData,
                                loading: true,
                            })
                        );
                        this.props.dispatch(
                            canvasActions.populateInitialBlocks({
                                initialBlocksState: blocks,
                            })
                        );

                        if (sessionData.userData.features.EMAIL_DESIGNER_CUSTOM_FONTS) {
                            this._loadCustomFonts();
                        }

                        if (sessionData.collaboration.hasStaffbaseAccount) {
                            this.props.dispatch(triggerFetch('config'));
                        }

                        eventService.sendMounted();
                        _trackSessions(sessionData);
                        pendoInit(sessionData.userData);
                        this._socketConnect(sessionData);
                    });
                }
            } else if (type === 'save') {
                eventService.triggerSave();
            } else if (type === 'saved') {
                setIsSaveFired(false);
                const { isConnected } = this.props.session.collaboration;
                saveTimeout.clear();
                if (isConnected) {
                    this.props.dispatch(draftSaved());
                }
                this.props.dispatch(updateVersionHistoryItemSavingStatus({ isSaving: false }));
                // stopAutoSaveTrigger();
            } else if (type === 'saveFailed') {
                setIsSaveFired(false);
                // if save failed reset the timer and trigger an autosave after countdown
                startAutoSaveTrigger();
                resetAutoSaveTimer();
            } else if (type === 'personalization_tags_updated') {
                const { dispatch } = this.props;
                const singleUseTagData = JSON.parse(payload);

                const singleUseTags = {};
                if (singleUseTagData?.attributes?.length) {
                    /**
                     * Note: We are appending default tags of `firstName` and `lastName` to the single use tags as a way to
                     * support previewing of fallback and preview data correctly. As of now, this change allows for the preview data to show the correct data as
                     * returned by the getSingleUseTags endpoint, as well as allowing for the fallback data stored in dynamo to be displayed correctly.
                     * It is not the ideal way to do things as we should be keeping these separate, and will need to be revisted later.
                     */
                    [...['firstName', 'lastName'], ...singleUseTagData.attributes].forEach((tag) => {
                        const camelCaseTag = _camelCase(tag);
                        singleUseTags[camelCaseTag] = {
                            name: camelCaseTag,
                            fallback: singleUseTagData.fallbackData?.[tag] || '',
                            previewData: {},
                            type: PersonalizationTagTypes.ONETIME,
                            isEnabled: true,
                        };
                        singleUseTagData.previewData.forEach((item) => {
                            singleUseTags[camelCaseTag].previewData = {
                                ...singleUseTags[camelCaseTag].previewData,
                                [item.uniqueID]: item[tag] || '',
                            };
                        });
                    });

                    dispatch(getSingleUseTagsSuccess(singleUseTags));
                } else if (singleUseTagData.error) {
                    dispatch(getSingleUseTagsError({ message: singleUseTagData.error }));
                }
            }
        }
    }

    render() {
        const { hasError, isSupported, browserWidth, browserHeight, browserWidthSmall, browserHeightSmall } =
            this.state;
        const {
            session: { loading },
        } = this.props;

        if (hasError) {
            return (
                <ThemeProvider theme={{ colors: COLORS }}>
                    <Styled.ErrorPageDiv>
                        <Styled.ErrorImage alt="error" src={imageSources.APP_FATAL_CRASH} />
                        <Styled.ErrorParagraph>Oops. Something went wrong...</Styled.ErrorParagraph>
                        <div>
                            <Styled.ErrorContactUsParagraph>
                                If the problem persists, please contact{' '}
                                <a style={{ color: '#0679BE' }} href="https://support.staffbase.com/">
                                    customer support
                                </a>{' '}
                                .
                            </Styled.ErrorContactUsParagraph>
                        </div>
                    </Styled.ErrorPageDiv>
                </ThemeProvider>
            );
        }

        if (!isSupported) {
            return (
                <h1>
                    Email Designer is not supported on your browser. Please consider upgrading your browser to its
                    latest version
                </h1>
            );
        }

        if (browserWidthSmall || browserHeightSmall) {
            const headlineText = `Oops. This frame ${browserWidthSmall ? 'width' : 'height'} is too small`;
            return (
                <ThemeProvider theme={{ colors: COLORS }}>
                    <Styled.BrowserSmallDiv>
                        <Styled.BrowserSmallTextDiv>
                            <Styled.TopText>{headlineText}</Styled.TopText>
                            <Styled.BottomText>
                                Please make your browser window bigger or zoom out to unleash your creativity
                            </Styled.BottomText>
                        </Styled.BrowserSmallTextDiv>
                        <Styled.FrameImageDiv>
                            <Styled.FrameImage src={imageSources.BROWSER_SMALL_PIKASSO} alt="pikasso" />
                        </Styled.FrameImageDiv>
                    </Styled.BrowserSmallDiv>
                </ThemeProvider>
            );
        }

        if (loading && !inIframe()) {
            if (window.location.href.indexOf('localhost') > 0 || window.location.href.indexOf('3000') > 0) {
                return (
                    <div
                        style={{
                            textAlign: 'center',
                            width: '100%',
                            height: '100%',
                            backgroundColor: '#fff',
                        }}
                    >
                        <img
                            src="https://app-data-legacy.bananatag.com/pikasso/assets/loader/transparent.gif"
                            alt="loader"
                        />
                        <div
                            style={{
                                width: '20%',
                                backgroundColor: '#eee',
                                border: '2px solid #0679BE',
                                margin: '50px auto',
                                padding: 5,
                                cursor: 'pointer',
                            }}
                            onClick={() => {
                                this.props.dispatch(sessionActions.setLoading(false));
                            }}
                            onKeyDown={(e) =>
                                handleKeyPress(e, () => this.props.dispatch(sessionActions.setLoading(false)))
                            }
                            role="button"
                            tabIndex={0}
                        >
                            <p
                                style={{
                                    textAlign: 'center',
                                    fontSize: 14,
                                    fontWeight: 'bold',
                                }}
                            >
                                Enter Pikasso
                            </p>
                        </div>
                    </div>
                );
            }

            return <h1>ACCESS DENIED</h1>;
        }

        if (!loading) {
            return (
                <ThemeProvider theme={{ colors: COLORS }}>
                    <Save />
                    <Hotkeys />
                    <Editor browserWidth={browserWidth} browserHeight={browserHeight} />
                    <Modals />
                </ThemeProvider>
            );
        }

        return (
            <div style={{ width: 150, margin: '150px auto' }}>
                <img
                    src="https://app-data-legacy.bananatag.com/pikasso/assets/loader/banana-loader.gif"
                    alt="loader"
                    width="150px"
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    session: state.session,
    changeLog: state.changeLog,
});

const mapDispatchToProps = (dispatch) => ({
    connectSocket: (token, plan) => dispatch(connectSocket(token, plan, dispatch)),
    listenForClients: () => dispatch(listenForClients(dispatch)),
    listenForUnsavedChanges: () => dispatch(listenForUnsavedChanges(dispatch)),
    listenForReconnect: (user) => dispatch(listenForReconnect(user, dispatch)),
    draftOpened: (user) => dispatch(draftOpened(user)),
    listenForChanges: () => dispatch(listenForChanges(dispatch)),
    listenForSaves: ({ documentID }) => dispatch(listenForSaves({ documentID, dispatch })),
    dispatch,
});

App.propTypes = {
    dispatch: PropTypes.func.isRequired,
    changeLog: PropTypes.string.isRequired,
    session: sessionType.isRequired,
    connectSocket: PropTypes.func.isRequired,
    listenForClients: PropTypes.func.isRequired,
    listenForChanges: PropTypes.func.isRequired,
    listenForReconnect: PropTypes.func.isRequired,
    listenForSaves: PropTypes.func.isRequired,
    listenForUnsavedChanges: PropTypes.func.isRequired,
    draftOpened: PropTypes.func.isRequired,
    ldClient: PropTypes.object,
    flags: PropTypes.object.isRequired,
};

App.defaultProps = {
    ldClient: undefined,
};

export default configs.reactAppPreview
    ? connect(mapStateToProps, mapDispatchToProps)(App)
    : withLDConsumer()(connect(mapStateToProps, mapDispatchToProps)(App));
